import { useEffect, useState } from 'react';

import { request } from '../../shared/api';
import { shape, type PricePoint } from './price-chart';

/**
 * История цены (T11.9, `09_UI_UX.md` §4.4).
 *
 * ## Зачем она в карточке
 *
 * Цена сама по себе не говорит, торгуется ли продавец. Объявление,
 * подешевевшее трижды за месяц, и объявление, стоящее по той же цене с
 * весны, — это два разных разговора, а выглядят они одинаково. График
 * показывает разницу за один взгляд.
 *
 * ## Отсутствие изменений — тоже сведение
 *
 * Когда точек меньше двух, блок не пропадает, а говорит прямо: цена не
 * менялась. Спрятать его значило бы оставить человека гадать, то ли
 * история пуста, то ли мы её не показали.
 *
 * ## Отказ не портит карточку
 *
 * История — довесок к объявлению, которое уже показано. Отказ этого
 * запроса убирает блок молча: сообщение об ошибке посреди карточки
 * поставило бы перед человеком задачу, которой у него нет.
 */

interface History {
  readonly listing_id: string | number;
  readonly currency: string;
  readonly window_days: number | null;
  readonly truncated: boolean;
  readonly points: readonly PricePoint[];
}

/** Размеры в собственных единицах картинки, а не в точках экрана. */
const WIDTH = 320;
const HEIGHT = 96;

function rubles(minor: number): string {
  return `${Math.round(minor / 100).toLocaleString('ru-RU')} ₽`;
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export function PriceHistory({
  listingId,
}: {
  listingId: string | number;
}): React.JSX.Element | null {
  const id = String(listingId);
  const [history, setHistory] = useState<History | null>(null);

  useEffect(() => {
    let alive = true;

    request<History>(`/v1/listings/${id}/price-history`)
      .then((found) => {
        if (alive) setHistory(found);
      })
      .catch(() => {
        if (alive) setHistory(null);
      });

    return () => {
      alive = false;
    };
  }, [id]);

  if (history === null) return null;

  const drawn = shape(history.points, Date.now(), WIDTH, HEIGHT);

  if (drawn === null) {
    const since = history.points[0]?.at;

    return (
      <div className="card block">
        <p className="block__title">История цены</p>
        <p className="block__hint">
          {since === undefined
            ? 'Цена ни разу не менялась.'
            : `Цена не менялась с ${day(since)} — продавец держится своей.`}
        </p>
      </div>
    );
  }

  const cheaper = drawn.change < 0;
  const firstPrice = Number(history.points[0]?.price_minor ?? 0);
  const lastPrice = Number(history.points.at(-1)?.price_minor ?? 0);

  return (
    <div className="card block">
      <p className="block__title">История цены</p>

      <p className="block__lead">
        {drawn.change === 0 ? (
          'Цена вернулась к прежней'
        ) : (
          <span className={cheaper ? 'chart__change chart__change--good' : 'chart__change'}>
            {cheaper ? '−' : '+'}
            {rubles(Math.abs(drawn.change))}
          </span>
        )}
      </p>

      {/*
        `preserveAspectRatio="none"` растягивает картинку по ширине
        карточки, а `non-scaling-stroke` в стилях не даёт растянуться
        вместе с ней толщине линии.

        Картинка декоративна: всё, что она показывает, названо словами
        рядом. Отсюда `aria-hidden`, а не подпись, которую чтецу экрана
        всё равно нечем прочесть.
      */}
      <svg
        className="chart"
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path className="chart__area" d={drawn.area} />
        <path
          className={cheaper ? 'chart__line chart__line--good' : 'chart__line'}
          d={drawn.path}
        />
      </svg>

      <p className="chart__axis">
        <span>
          {day(drawn.from)} · {rubles(firstPrice)}
        </span>
        <span>сейчас · {rubles(lastPrice)}</span>
      </p>

      {history.truncated ? (
        <p className="block__hint">
          {history.window_days === null
            ? 'Показаны не все изменения: их слишком много.'
            : `На бесплатном тарифе видно ${String(history.window_days)} дней. Раньше цена тоже менялась.`}
        </p>
      ) : null}
    </div>
  );
}
